import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import Swal from 'sweetalert2';
import { toast } from '../../../common/Toast/toast';
import EventDetailModal from './EventDetailModal.jsx';
import './CalendarWithPopup.css';
/* The gate below is the sp-gate block, not a copy of it -- the same one
   /student-profile and /student-id-card draw, so the three cannot drift
   apart. */
import '../../../Student/StudentProfile/StudentProfile.css';
// The shared page kit: shell, filter panel, buttons, dialog, states.
import '../../../Trainer/TrainerDashboard.css';
import moment from 'moment';
import { useIsMobile } from '../../../../../utils/useIsMobile';

// Import API services
import { getClassrooms, getClassroomById } from '../../../../../services/ClassroomService.js';
import { getBatchOptions, getBatchById } from '../../../../../services/BatchService';
import { getCalendarEvents, getCalendarDayCounts } from '../../../../../services/CalendarService';
import AddEventModal from '../../Event_Form/AddEventForm.jsx';
import DayEventsModal from './DayEventsModal.jsx';
import PopupDone, { usePopupDone } from '../../../common/PopupDone/PopupDone.jsx';
import { getTrainers, getTrainerByTrainerId } from '../../../../../services/TrainerService';
import AsyncSelectDropdown from '../../../common/NewCustomAsyncSelectDropdown.jsx';
import * as XLSX from 'xlsx';
import { useAuth } from '../../../../../context/AuthContext';
import { getStudentDashboard } from '../../../../../services/StudentDashboardService';
import { getStudentOptions } from '../../../../../services/StudentService';
import { CalendarDays, Search } from 'lucide-react';

const BATCH_FIELD_PLACEHOLDERS = new Set(['na', 'n/a', 'null', 'undefined', 'tbd', '-']);

const isPlaceholderBatchField = (v) => {
  const t = String(v ?? '')
    .trim()
    .toLowerCase();
  return BATCH_FIELD_PLACEHOLDERS.has(t);
};

const formatBatchMetaField = (v) => {
  const s = String(v ?? '').trim();
  if (!s || isPlaceholderBatchField(s)) return '';
  return s;
};

const normalizeCalendarUrl = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s || isPlaceholderBatchField(s)) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return s;
  if (/^[\w.-]+\.[a-z]{2,}/i.test(s)) return `https://${s}`;
  return s;
};

// Caps a trainer/batch name list (array, or a comma-joined string as the
// backend sends trainerName/batchInfo) to the first `limit` names, appending
// "and N more" instead of dumping every name in the popup.
const summarizeNameList = (namesInput, limit = 3) => {
  const names = Array.isArray(namesInput)
    ? namesInput.map((n) => String(n ?? '').trim()).filter(Boolean)
    : String(namesInput ?? '')
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean);
  if (names.length <= limit) return names.join(', ');
  const remaining = names.length - limit;
  return `${names.slice(0, limit).join(', ')} and ${remaining} more`;
};

// ─── Event colour, washed out ─────────────────────────────────────
// FullCalendar paints each chip in the solid colour the backend sends,
// which on a full month reads as a wall of saturated blocks. These turn
// that one colour into three: a soft, cotton-weight fill, a slightly
// stronger hairline, and a dark version of the same hue for the text --
// so a day of six events is still legible and the colour still says
// which kind of event it is.
//
// It is done in JS rather than CSS because the source colour arrives
// inline, per event, and CSS cannot derive a tint from a value it
// cannot read.
const readColor = (input) => {
  const v = String(input || '').trim();
  const hex = v.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  const rgb = v.match(/rgba?\(([^)]+)\)/i);
  if (rgb) {
    const parts = rgb[1].split(',').map((n) => Number.parseFloat(n));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => !Number.isNaN(n))) {
      return parts.slice(0, 3).map((n) => Math.max(0, Math.min(255, n)));
    }
  }
  return null;
};

const mix = ([r, g, b], target, amount) => [
  Math.round(r + (target - r) * amount),
  Math.round(g + (target - g) * amount),
  Math.round(b + (target - b) * amount),
];

const rgbCss = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;

// WCAG 2.1 relative luminance and contrast ratio. Worth the dozen lines:
// the ink below used to be a flat "42% toward black", which keeps 58% of
// the source's luminance -- fine for a colour that starts dark, and far
// too pale for one that starts bright. A yellow event came out as light
// yellow text on a near-white chip.
const luminance = ([r, g, b]) =>
  [r, g, b]
    .map((c) => c / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
    .reduce((sum, c, i) => sum + c * [0.2126, 0.7152, 0.0722][i], 0);

const contrastRatio = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};

// Darken the event's own hue until it clears AA against the chip it sits
// on, so the text keeps saying which kind of event it is AND stays
// readable. Stops at 90%: past that every hue is the same near-black and
// the colour has stopped carrying meaning.
const inkOn = (rgb, ground) => {
  for (let amount = 0.45; amount < 0.9; amount += 0.05) {
    const ink = mix(rgb, 0, amount);
    if (contrastRatio(ink, ground) >= 4.5) return ink;
  }
  return mix(rgb, 0, 0.9);
};

// The nodes FullCalendar actually paints the text on. `.fc-event-main` is
// the one that carries the colour rule (`.fc-h-event .fc-event-main` in
// core, `.fc-v-event .fc-event-main` in timegrid); the title and time are
// listed too because a stylesheet may colour them directly, and setting
// the value on the node itself is the only thing no selector can outrank.
const EVENT_TEXT_NODES = '.fc-event-main, .fc-event-main-frame, .fc-event-title, .fc-event-time, .fc-event-title-container';

const paintEventCotton = (info) => {
  const el = info?.el;
  if (!el) return;
  const source =
    info.event?.backgroundColor ||
    info.event?.borderColor ||
    el.style.backgroundColor ||
    getComputedStyle(el).backgroundColor;
  // No readable colour on the event is not a reason to leave white text on
  // a chip this stylesheet paints pale. Fall back to the ink the rest of
  // the app uses for body text.
  const rgb = readColor(source);
  const fill = rgb ? mix(rgb, 255, 0.86) : null;
  const ink = rgb ? inkOn(rgb, fill) : [16, 24, 40];

  if (rgb) {
    el.style.setProperty('background-color', rgbCss(fill), 'important');
    el.style.setProperty('border-color', rgbCss(mix(rgb, 255, 0.62)), 'important');
    // The one piece of full-strength colour left: a bar down the side, so
    // the hue is still readable at a glance on a pale chip.
    el.style.setProperty('--cal-event-accent', rgbCss(mix(rgb, 0, 0.08)));
  }

  el.style.setProperty('color', rgbCss(ink), 'important');
  el.style.setProperty('--fc-event-text-color', rgbCss(ink));

  // And on the text nodes themselves. The colour above is set on the event
  // element, but FullCalendar paints the text through its own rule on a
  // CHILD -- `.fc-v-event .fc-event-main { color: var(--fc-event-text-color) }`
  // -- and a rule targeting the child is not something a parent's inline
  // style can reach past. Feeding the variable fixes that in the normal
  // case; writing the value onto the nodes as well is what makes it hold
  // whatever else any stylesheet, now or later, says about them.
  el.querySelectorAll(EVENT_TEXT_NODES).forEach((node) => {
    node.style.setProperty('color', rgbCss(ink), 'important');
    node.style.setProperty('text-shadow', 'none', 'important');
  });
};

/* What a chip says: its start time as a clock reads it -- "1:30 PM" --
   then its title. All-day entries (holidays) have no time.

   The time slot used to be overwritten with the batch code after the chip
   mounted. Nobody reads a code as a time: chips showed "8765" where the
   start time belonged, and because FullCalendar re-renders that node
   itself, often the code and the time jammed together ("8601:30am").
   Rendering the content here gives FullCalendar the text to draw, so
   nothing is overwritten afterwards. The markup is FullCalendar's own,
   so every chip style still applies. */
const renderEventChip = (arg) => {
  const start = arg.event.start;
  const lead = !arg.event.allDay && start ? moment(start).format('h:mm A') : '';
  return (
    <div className="fc-event-main-frame">
      {lead && <div className="fc-event-time">{lead}</div>}
      <div className="fc-event-title-container">
        <div className="fc-event-title fc-sticky">{arg.event.title || ' '}</div>
      </div>
    </div>
  );
};

// How many entries fall on each date of the loaded view. The grid shows
// chips and "+N more" now; this count is what the day list compares its
// own result against when filters are on.
//
// A multi-day holiday counts on every date it covers, so the number for a
// date is what the day list for that date will actually show.
const DAY_SPAN_CAP = 400;

const isHolidayEvent = (ev) => {
  const props = ev?.extendedProps || {};
  return Boolean(props.isHoliday) || props.category === 'Holiday';
};

const countByDay = (events) => {
  const counts = new Map();
  for (const ev of events || []) {
    const start = ev?.start ? new Date(ev.start) : null;
    if (!start || Number.isNaN(start.getTime())) continue;

    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const rawEnd = ev?.end ? new Date(ev.end) : null;
    let last = cursor;

    if (rawEnd && !Number.isNaN(rawEnd.getTime()) && rawEnd > start) {
      // FullCalendar's end is exclusive for all-day spans, so a holiday
      // that ends at midnight does not spill into the following date.
      const shifted = new Date(rawEnd.getTime() - (ev.allDay ? 1 : 0));
      last = new Date(shifted.getFullYear(), shifted.getMonth(), shifted.getDate());
    }

    const holiday = isHolidayEvent(ev);

    for (let i = 0; cursor <= last && i < DAY_SPAN_CAP; i += 1) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(
        cursor.getDate(),
      ).padStart(2, '0')}`;
      const cell = counts.get(key) || { n: 0, holiday: false };
      cell.n += 1;
      // One holiday anywhere in the date is enough to colour it: that is
      // the thing a reader scanning a month is looking for.
      if (holiday) cell.holiday = true;
      counts.set(key, cell);
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return counts;
};


const AdminCalendar = ({ audience }) => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true || user?.isSuperAdmin === 'true';
  const isAdminMode = Boolean(isSuperAdmin || hasPermission('admin-calendar', 'view', 'lms'));
  const isTrainerMode = Boolean(
    !isAdminMode &&
      (hasPermission('trainer-calendar', 'view', 'lms') ||
        hasPermission('trainer-calender', 'view', 'lms'))
  );
  const isStudentMode = Boolean(
    !isAdminMode &&
      !isTrainerMode &&
      hasPermission('student-calendar', 'view', 'lms')
  );
  const canManageEvents = isAdminMode || isTrainerMode;
  const canExport = isAdminMode;

  /* Whose calendar this route is. /student-calendar and
     /attendance-calendar say "student"; /admin-calendar and
     /trainer-calendar say nothing and mean the whole estate.

     It has to be stated, because the roles cannot answer it: an admin
     reading a student's calendar is still an admin, so isAdminMode is
     true on both routes and the page had no way to tell them apart. It
     therefore drew the whole institution's calendar under the heading
     "Class calendar", with no indication whose it was supposed to be. */
  const studentAudience = audience === 'student';

  /* Staff on the student calendar name a student FIRST -- the same gate
     /student-id-card and /student-profile draw. Nothing is fetched
     behind it: a calendar that loads the estate and then narrows it has
     already answered the wrong question, and on a busy month that is a
     full view's worth of rows fetched to be thrown away. */
  /* Admins only, and that is the API's rule rather than a choice made
     here: /studentdashboard honours `studentId` and `email` only
     `if (isAdmin)` (studentdashboard.service.js:45-46) and otherwise
     silently answers for the CALLER. A trainer sent through this gate
     would pick a student, be handed their own record back, and get a
     calendar scoped to batches that are not the student's -- in
     practice an empty one, with nothing on screen saying why.

     A trainer on this route keeps the unscoped calendar they had
     before. Scoping their view properly needs the API to accept the
     lookup from them too. */
  const needsStudentPick = studentAudience && isAdminMode;
  const [pickedStudent, setPickedStudent] = useState(null);

  /* The gate's dropdown. Paged and searched on the server, like every
     other student picker in the app -- the roll is far too long to hold
     in memory and filter client-side. */
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentOptionsLoading, setStudentOptionsLoading] = useState(false);
  const [studentPage, setStudentPage] = useState(1);
  const [studentHasMore, setStudentHasMore] = useState(true);
  const [studentSearch, setStudentSearch] = useState('');
  const studentFetchRef = useRef(false);
  const studentOpenedRef = useRef(false);

  const loadStudentOptions = useCallback(async (page = 1, search = '', append = false) => {
    if (studentFetchRef.current) return;
    studentFetchRef.current = true;
    setStudentOptionsLoading(true);
    try {
      // Names only (server skips the join); no count -- paging is by page size.
      const res = await getStudentOptions(20, page, search, false);
      const list = Array.isArray(res?.data) ? res.data : [];
      const options = list
        .map((st) => ({
          value: String(st.email || st.id || '').trim(),
          label: `${st.name || 'Unnamed'} | ${st.email || ''}`.trim(),
          student: st,
        }))
        .filter((o) => o.value);
      // Guarded by value so a repeated page cannot double up an option.
      setStudentOptions((prev) => {
        if (!append) return options;
        const seen = new Set(prev.map((o) => o.value));
        return [...prev, ...options.filter((o) => !seen.has(o.value))];
      });
      setStudentHasMore(list.length === 20);
      setStudentPage(page);
    } catch (error) {
      console.error('[calendar] could not load the student roll:', error);
      if (!append) setStudentOptions([]);
      setStudentHasMore(false);
    } finally {
      setStudentOptionsLoading(false);
      studentFetchRef.current = false;
    }
  }, []);

  const handleStudentPicked = useCallback((picked) => {
    const option = Array.isArray(picked) && picked.length ? picked[0] : null;
    if (!option) {
      setPickedStudent(null);
      return;
    }
    const st = option.student || {};
    setPickedStudent({
      id: String(st.id || st._id || '').trim(),
      email: String(st.email || option.value || '').trim(),
      name: st.name || option.label,
    });
  }, []);
  const [studentScopedBatches, setStudentScopedBatches] = useState([]);
  /* The distinct trainers across this student's batches. The trainer
     filter offers these instead of the whole directory: on a student's
     calendar every other name in the institution narrows it to nothing,
     which is a control that can only be got wrong. */
  const [studentScopedTrainers, setStudentScopedTrainers] = useState([]);
  /* Whether the student's batch list has come back yet -- resolved, not
     merely empty. The scope below must not read an unloaded list as
     "this student has no batches", and must not read it as "show
     everything" either; it waits instead. */
  const [studentBatchesReady, setStudentBatchesReady] = useState(false);

  /* The ids behind the batch filter's options, as a set. Loaded for the
     picker already; this is the same list read as a permission. */
  const studentBatchIdSet = useMemo(
    () => new Set(studentScopedBatches.map((o) => String(o.value || '').trim()).filter(Boolean)),
    [studentScopedBatches],
  );

  /* Whose calendar this is, as the API takes it -- or null for the
     institution's.

     "What is on this student's calendar" belongs to the query, and the
     server answers it from the student alone (calendar.service.js reads
     their batches). This used to send every one of the student's batch
     ids in the URL -- 253 of them, 6 KB of query string, for one student
     -- a list the server reads in a single lookup. A student reading
     their own sends nothing: the server scopes them to their own record
     whatever is asked. Staff reading one student name the student, by
     email first -- the key both the roll and the billing record agree on
     (see the note on getStudentDashboard below). */
  const studentScopeParams = useMemo(() => {
    if (isStudentMode) return {};
    if (!pickedStudent) return null;
    return pickedStudent.email
      ? { studentEmail: pickedStudent.email }
      : { studentId: pickedStudent.id };
  }, [isStudentMode, pickedStudent]);

  /* The batch narrowing for a request. Inside a student's calendar the
     scope is not optional: a picked batch narrows it only when it is one
     of theirs, so a filter can never widen a calendar past what its
     reader may see. */
  const batchScope = useCallback(
    (pickedBatchId) => {
      if (!studentScopeParams) return { batchId: pickedBatchId || undefined };
      if (pickedBatchId && studentBatchIdSet.has(String(pickedBatchId))) {
        return { batchId: String(pickedBatchId) };
      }
      return { ...studentScopeParams };
    },
    [studentScopeParams, studentBatchIdSet],
  );

  /* Whether a scope is wanted but not yet answerable. The fetch waits
     rather than going out unnarrowed. */
  const scopePending =
    (isStudentMode || Boolean(pickedStudent)) && !studentBatchesReady;

  /* One student's calendar, whoever is reading it. The trainer filter
     is theirs rather than the institution's in both views. */
  const studentScopedView = isStudentMode || Boolean(pickedStudent);

  const [calendarEvents, setCalendarEvents] = useState([]);

  // Institution operating hours
  const INSTITUTION_START = 6; // 6 AM
  const INSTITUTION_END = 22; // 10 PM

  // Filter states
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [selectedClassroom, setSelectedClassroom] = useState('');
  const [startDateFilter, setStartDateFilter] = useState(() =>
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [endDateFilter, setEndDateFilter] = useState(() =>
    moment().endOf('month').format('YYYY-MM-DD')
  );
  const [eventTypeFilter, setEventTypeFilter] = useState('all'); // all, classroom, batch, event
  const [availabilityFilter, setAvailabilityFilter] = useState('all'); // all, available, unavailable
  const [hidePastEvents, setHidePastEvents] = useState(true);
  const [apiDayCounts, setApiDayCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(false);
  /* Phones only. The filter panel is eight controls; above the calendar
     on a 360px screen that is a full screen of form before the student
     reaches the month they came to look at. Below 768px it moves into a
     bottom sheet and this is whether that sheet is up. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(true); // Track if filters have been applied
  const calendarRef = useRef(null);
  const [currentViewRange, setCurrentViewRange] = useState({ start: null, end: null });
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  // The entry a chip was clicked on, or null. What <EventDetailModal>
  // reads -- the dialog used to be a SweetAlert, so there was no state
  // for it at all and closing it meant calling Swal.close() from wherever
  // you happened to be.
  const [eventDetail, setEventDetail] = useState(null);
  const [dayListDate, setDayListDate] = useState(null);
  const [selectedDateForEvent, setSelectedDateForEvent] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [batchFilterOptions, setBatchFilterOptions] = useState([]);
  const [batchFilterPage, setBatchFilterPage] = useState(1);
  const [batchFilterHasMore, setBatchFilterHasMore] = useState(true);
  const [batchFilterSearch, setBatchFilterSearch] = useState('');
  const [batchFilterLoading, setBatchFilterLoading] = useState(false);
  const [batchFilterFetchingMore, setBatchFilterFetchingMore] = useState(false);
  const [selectedBatchOption, setSelectedBatchOption] = useState(null);
  const batchFilterSeq = useRef(0);

  const [trainerFilterOptions, setTrainerFilterOptions] = useState([]);

  const [trainerFilterPage, setTrainerFilterPage] = useState(1);
  const [trainerFilterHasMore, setTrainerFilterHasMore] = useState(true);
  const [trainerFilterSearch, setTrainerFilterSearch] = useState('');
  const [trainerFilterLoading, setTrainerFilterLoading] = useState(false);
  const [trainerFilterFetchingMore, setTrainerFilterFetchingMore] = useState(false);
  const [selectedTrainerOption, setSelectedTrainerOption] = useState(null);
  const trainerFilterSeq = useRef(0);

  /* What the trainer picker lists. Declared HERE, under the trainer
     filter's own state, and not up with the other scope values: it reads
     `trainerFilterOptions`, and a const that reads a `useState` declared
     further down the component is a temporal-dead-zone error that throws
     on the first render for every reader who is not in student scope. */
  const trainerPickerOptions = studentScopedView
    ? [{ label: 'All Trainers', value: '' }, ...studentScopedTrainers]
    : isAdminMode || isTrainerMode
      ? [{ label: 'All Trainers', value: '' }, ...trainerFilterOptions]
      : [{ label: user?.name || 'Current User', value: '' }];

  const [classroomFilterOptions, setClassroomFilterOptions] = useState([]);
  const [classroomFilterPage, setClassroomFilterPage] = useState(1);
  const [classroomFilterHasMore, setClassroomFilterHasMore] = useState(true);
  const [classroomFilterSearch, setClassroomFilterSearch] = useState('');
  const [classroomFilterLoading, setClassroomFilterLoading] = useState(false);
  const [classroomFilterFetchingMore, setClassroomFilterFetchingMore] = useState(false);
  const [selectedClassroomOption, setSelectedClassroomOption] = useState(null);
  const classroomFilterSeq = useRef(0);

  // Enhanced color scheme
  const COLORS = {
    CLASSROOM: '#6f42c1', // Purple for all classroom events
    BATCH: '#28a745', // Green for all batch events
    EVENT: '#4F46E5', // Indigo for all regular events -- deliberately not
    // red: red against the green of BATCH is the one pairing red/green
    // colour blindness cannot separate, and those two are the types that
    // sit side by side on a student's calendar most often.
    MOCK_INTERVIEW: '#e5a000', // Golden for mock interviews
    FREE_SLOT: '#20c997', // Teal for free slots
    HOLIDAY: '#ff6b35', // Orange for holidays
  };

  useEffect(() => {
    if (isAdminMode || isTrainerMode || isStudentMode) return;
    toast.error('You do not have permission to access calendar.');
    navigate('/');
  }, [isAdminMode, isStudentMode, isTrainerMode, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // A student reads their own record; staff read the record of the
      // student they named at the gate. Same call, same shape, so the
      // scope below cannot tell the two apart -- which is the point.
      /* Email first. Both keys are accepted, but `studentId` is matched
         against the BILLING student record's id
         (studentdashboard.service.js:82-84), and the id the student roll
         hands back is not guaranteed to be that one -- where they differ
         the lookup finds nobody and the calendar comes back empty.
         Email is the key both sides agree on. */
      const target = isStudentMode
        ? {}
        : pickedStudent
          ? (pickedStudent.email
              ? { email: pickedStudent.email }
              : { studentId: pickedStudent.id })
          : null;
      if (!target) return;
      setStudentBatchesReady(false);
      try {
        const res = await getStudentDashboard(target);
        const batches = Array.isArray(res?.data?.batches) ? res.data.batches : [];
        const opts = batches
          .map((b) => ({ label: b?.name || String(b?.id || ''), value: String(b?.id || '') }))
          .filter((o) => String(o.value || '').trim());

        /* attachRelations replaces a batch's trainerId with the matched
           row (an array of them), and leaves the bare id there when it
           could not be looked up -- so both shapes are read. The name is
           the decorated trainerName the same response carries, which is
           what the batch cards already print. */
        const seenTrainers = new Set();
        const trainerOpts = [];
        for (const b of batches) {
          const raw = Array.isArray(b?.trainerId) ? b.trainerId[0] : b?.trainerId;
          const id = String(
            (raw && typeof raw === 'object' ? raw.id || raw._id : raw) || '',
          ).trim();
          if (!id || seenTrainers.has(id)) continue;
          seenTrainers.add(id);
          const named =
            (raw && typeof raw === 'object' && String(raw.trainerName || '').trim()) ||
            String(b?.trainerName || '').trim();
          // "Not assigned" is the placeholder the dashboard puts on a
          // batch with no trainer; it is not a person to filter by.
          if (!named || named === 'Not assigned') continue;
          trainerOpts.push({ label: named, value: id });
        }
        trainerOpts.sort((a, b) => a.label.localeCompare(b.label));

        if (!cancelled) {
          setStudentScopedBatches(opts);
          setStudentScopedTrainers(trainerOpts);
          setStudentBatchesReady(true);
        }
      } catch (_) {
        if (!cancelled) {
          setStudentScopedBatches([]);
          setStudentScopedTrainers([]);
          // Resolved, even though it failed: an error is not a reason to
          // fall back to showing the whole institution's calendar.
          setStudentBatchesReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isStudentMode, pickedStudent]);

  const mergeSelectOptions = (prev, next) => {
    const map = new Map();
    (prev || []).forEach((o) => map.set(String(o?.value), o));
    (next || []).forEach((o) => map.set(String(o?.value), o));
    return Array.from(map.values()).filter((o) => o && String(o.value || '').trim());
  };

  const toBatchSelectOption = (batch) => {
    if (!batch) return null;
    const value = String(batch.id || batch._id || '').trim();
    if (!value) return null;
    return { label: batch.name || value, value };
  };

  const toTrainerSelectOption = (t) => {
    if (!t) return null;
    const value = String(t.trainerId || '').trim();
    if (!value) return null;
    const name = t.trainerName || value;
    const email = t.trainerEmail ? ` (${t.trainerEmail})` : '';
    const spec = t.specialization ? ` - ${t.specialization}` : '';
    return { label: `${name}${email}${spec}`, value };
  };

  const toClassroomSelectOption = (c) => {
    if (!c) return null;
    const value = String(c.id || c._id || '').trim();
    if (!value) return null;
    const no = c.classroomNo || value;
    const desc = c.description ? ` - ${c.description}` : '';
    return { label: `${no}${desc}`, value };
  };

  const fetchBatchFilterOptionsPage = async ({ page = 1, search = '', append = false } = {}) => {
    const seq = (batchFilterSeq.current += 1);
    setBatchFilterLoading(true);
    try {
      if (isStudentMode) {
        if (seq !== batchFilterSeq.current) return [];
        const term = String(search || '').toLowerCase();
        const filtered = term
          ? studentScopedBatches.filter((o) => String(o.label || '').toLowerCase().includes(term))
          : studentScopedBatches;
        const start = (page - 1) * 10;
        const chunk = filtered.slice(start, start + 10);
        const hasNext = start + 10 < filtered.length;
        setBatchFilterOptions((prev) => (append ? mergeSelectOptions(prev, chunk) : chunk));
        setBatchFilterPage(page);
        setBatchFilterSearch(search);
        setBatchFilterHasMore(Boolean(hasNext));
        return chunk;
      }

      const trainerId = isTrainerMode ? user?.id : undefined;
      if (isTrainerMode && !trainerId) {
        if (seq !== batchFilterSeq.current) return [];
        setBatchFilterOptions((prev) => (append ? prev : []));
        setBatchFilterHasMore(false);
        return [];
      }
      // Names only: this is a picker (see getBatchOptions).
      const resp = await getBatchOptions(10, page, search, trainerId || undefined);
      if (seq !== batchFilterSeq.current) return [];
      const next = (Array.isArray(resp?.data) ? resp.data : []).map(toBatchSelectOption).filter(Boolean);
      setBatchFilterOptions((prev) => (append ? mergeSelectOptions(prev, next) : next));
      setBatchFilterPage(page);
      setBatchFilterSearch(search);
      setBatchFilterHasMore(Boolean(resp?.pagination?.hasNext));
      return next;
    } catch (_) {
      if (seq !== batchFilterSeq.current) return [];
      setBatchFilterOptions([]);
      setBatchFilterHasMore(false);
      return [];
    } finally {
      if (seq === batchFilterSeq.current) setBatchFilterLoading(false);
    }
  };

  const loadBatchFilterOptions = async (inputValue) => {
    const search = String(inputValue || '');
    setBatchFilterPage(1);
    return await fetchBatchFilterOptionsPage({ page: 1, search, append: false });
  };

  const handleBatchFilterMenuOpen = async () => {
    if (batchFilterOptions.length > 0) return;
    if (batchFilterLoading) return;
    await fetchBatchFilterOptionsPage({ page: 1, search: '', append: false });
  };

  const handleBatchFilterMenuScrollToBottom = async () => {
    if (batchFilterLoading || batchFilterFetchingMore) return;
    if (!batchFilterHasMore) return;
    setBatchFilterFetchingMore(true);
    try {
      await fetchBatchFilterOptionsPage({ page: batchFilterPage + 1, search: batchFilterSearch, append: true });
    } finally {
      setBatchFilterFetchingMore(false);
    }
  };

  const fetchTrainerFilterOptionsPage = async ({ page = 1, search = '', append = false } = {}) => {
    const seq = (trainerFilterSeq.current += 1);
    setTrainerFilterLoading(true);
    try {
      if (!isAdminMode && !isTrainerMode) {
        if (seq !== trainerFilterSeq.current) return [];
        setTrainerFilterOptions([]);
        setTrainerFilterPage(1);
        setTrainerFilterSearch('');
        setTrainerFilterHasMore(false);
        return [];
      }
      const resp = await getTrainers(10, page, search, false);
      if (seq !== trainerFilterSeq.current) return [];
      const next = (Array.isArray(resp?.data) ? resp.data : []).map(toTrainerSelectOption).filter(Boolean);
      setTrainerFilterOptions((prev) => (append ? mergeSelectOptions(prev, next) : next));
      setTrainerFilterPage(page);
      setTrainerFilterSearch(search);
      setTrainerFilterHasMore(Boolean(resp?.pagination?.hasNext));
      return next;
    } catch (_) {
      if (seq !== trainerFilterSeq.current) return [];
      setTrainerFilterOptions([]);
      setTrainerFilterHasMore(false);
      return [];
    } finally {
      if (seq === trainerFilterSeq.current) setTrainerFilterLoading(false);
    }
  };

  const loadTrainerFilterOptions = async (inputValue) => {
    const search = String(inputValue || '');
    setTrainerFilterPage(1);
    return await fetchTrainerFilterOptionsPage({ page: 1, search, append: false });
  };

  const handleTrainerFilterMenuOpen = async () => {
    if (trainerFilterOptions.length > 0) return;
    if (trainerFilterLoading) return;
    await fetchTrainerFilterOptionsPage({ page: 1, search: '', append: false });
  };

  const handleTrainerFilterMenuScrollToBottom = async () => {
    if (trainerFilterLoading || trainerFilterFetchingMore) return;
    if (!trainerFilterHasMore) return;
    setTrainerFilterFetchingMore(true);
    try {
      await fetchTrainerFilterOptionsPage({ page: trainerFilterPage + 1, search: trainerFilterSearch, append: true });
    } finally {
      setTrainerFilterFetchingMore(false);
    }
  };

  const fetchClassroomFilterOptionsPage = async ({ page = 1, search = '', append = false } = {}) => {
    const seq = (classroomFilterSeq.current += 1);
    setClassroomFilterLoading(true);
    try {
      const resp = await getClassrooms(10, page, search, false);
      if (seq !== classroomFilterSeq.current) return [];
      const next = (Array.isArray(resp?.data) ? resp.data : []).map(toClassroomSelectOption).filter(Boolean);
      setClassroomFilterOptions((prev) => (append ? mergeSelectOptions(prev, next) : next));
      setClassroomFilterPage(page);
      setClassroomFilterSearch(search);
      setClassroomFilterHasMore(Boolean(resp?.pagination?.hasNext));
      return next;
    } catch (_) {
      if (seq !== classroomFilterSeq.current) return [];
      setClassroomFilterOptions([]);
      setClassroomFilterHasMore(false);
      return [];
    } finally {
      if (seq === classroomFilterSeq.current) setClassroomFilterLoading(false);
    }
  };

  const loadClassroomFilterOptions = async (inputValue) => {
    const search = String(inputValue || '');
    setClassroomFilterPage(1);
    return await fetchClassroomFilterOptionsPage({ page: 1, search, append: false });
  };

  const handleClassroomFilterMenuOpen = async () => {
    if (classroomFilterOptions.length > 0) return;
    if (classroomFilterLoading) return;
    await fetchClassroomFilterOptionsPage({ page: 1, search: '', append: false });
  };

  const handleClassroomFilterMenuScrollToBottom = async () => {
    if (classroomFilterLoading || classroomFilterFetchingMore) return;
    if (!classroomFilterHasMore) return;
    setClassroomFilterFetchingMore(true);
    try {
      await fetchClassroomFilterOptionsPage({
        page: classroomFilterPage + 1,
        search: classroomFilterSearch,
        append: true,
      });
    } finally {
      setClassroomFilterFetchingMore(false);
    }
  };

  const exportToExcel = () => {
    if (!calendarEvents || calendarEvents.length === 0) {
      toast.warning('No events available to export. Please apply filters first.');
      return;
    }

    // Transform calendar events to Excel-friendly format
    const excelData = calendarEvents.map((event) => {
      const startDate = moment(event.start);
      const endDate = event.end ? moment(event.end) : startDate;

      // Determine event type based on color or extendedProps
      let eventType = 'Event';
      let eventDetails = '';
      let batchName = '';
      let trainerName = '';
      let classroom = '';
      let course = '';

      if (event.extendedProps) {
        const props = event.extendedProps;

        // Determine event type
        if (props.type === 'batch_daily') {
          eventType = 'Batch Session';
          batchName = props.batchName || '';
          trainerName =
            typeof props.trainers === 'object' ? props.trainers.name : props.trainers || '';
          classroom = props.venue || '';
          course = typeof props.course === 'object' ? props.course.name : props.course || '';
          eventDetails = `Day ${props.dayNumber} of ${props.totalDays} - ${props.description || ''}`;
        } else if (props.type === 'classroom') {
          eventType = 'Classroom';
          classroom = props.classroomNo || '';
          eventDetails = props.description || '';
        } else if (props.type === 'event') {
          eventType = 'Regular Event';
          eventDetails = props.description || '';
          trainerName = Array.isArray(props.trainers)
            ? props.trainers.join(', ')
            : props.trainers || '';
          classroom = props.venue || '';
          batchName = Array.isArray(props.batches) ? props.batches.join(', ') : props.batches || '';
        }
      }

      return {
        'Event Title': event.title?.replace(/[📚📅✅❌]/g, '').trim() || 'No Title',
        'Event Type': eventType,
        'Start Date': startDate.format('YYYY-MM-DD'),
        'Start Time': startDate.format('HH:mm'),
        'End Date': endDate.format('YYYY-MM-DD'),
        'End Time': endDate.format('HH:mm'),
        'Duration (hours)': endDate.diff(startDate, 'hours', true).toFixed(2),
        Batch: batchName,
        Trainer: trainerName,
        Classroom: classroom,
        Course: course,
        Description: eventDetails,
        Status: event.extendedProps?.isAvailable ? 'Available' : 'Not Available',
        'Background Color': event.backgroundColor || '',
        Mode: event.extendedProps?.mode || '',
        Organizer: event.extendedProps?.organizer || '',
        Category: event.extendedProps?.category || '',
      };
    });

    // Generate filename with current date and filter info
    const getFilterInfo = () => {
      let info = [];
      if (eventTypeFilter !== 'all') info.push(eventTypeFilter);
      if (selectedBatch) info.push(`Batch-${selectedBatchOption?.label || selectedBatch}`);
      if (selectedTrainer) info.push('Trainer-Filter');
      if (selectedClassroom) info.push(`Classroom-${selectedClassroomOption?.label || selectedClassroom}`);
      if (startDateFilter) info.push(`From-${startDateFilter}`);
      if (endDateFilter) info.push(`To-${endDateFilter}`);
      return info.length > 0 ? info.join('_') : 'All-Filters';
    };

    const fileName = `Calendar_Export_${moment().format('YYYY-MM-DD_HH-mm')}_${getFilterInfo()}`;

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidths = {};
    excelData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const value = String(row[key] || '');
        maxWidths[key] = Math.max(maxWidths[key] || 0, value.length);
      });
    });

    worksheet['!cols'] = Object.keys(maxWidths).map((key) => ({
      wch: Math.min(Math.max(maxWidths[key], key.length), 50),
    }));

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Calendar Events');

    // Add metadata
    const metadata = [
      ['Calendar Export Report'],
      ['Generated on', moment().format('LLLL')],
      ['Total Events', calendarEvents.length],
      [''],
      ['Applied Filters:'],
      ['Event Type', eventTypeFilter],
      ['Batch', selectedBatch ? selectedBatchOption?.label || selectedBatch : 'All'],
      ['Trainer', selectedTrainer ? selectedTrainerOption?.label || selectedTrainer : 'All'],
      ['Classroom', selectedClassroom ? selectedClassroomOption?.label || selectedClassroom : 'All'],
      ['Date Range', `${startDateFilter || 'Any'} to ${endDateFilter || 'Any'}`],
      ['Hide Past Events', hidePastEvents ? 'Yes' : 'No'],
      [''],
    ];

    const metadataSheet = XLSX.utils.aoa_to_sheet(metadata);
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Report Info');

    // Export file
    XLSX.writeFile(workbook, `${fileName}.xlsx`);

    // Show success message
    toast.success(`Exported ${calendarEvents.length} events to ${fileName}.xlsx`, { toast: true });
  };

  // Fetch all data on component mount
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (!selectedBatch) {
      setSelectedBatchOption(null);
      return;
    }
    const found = (batchFilterOptions || []).find((o) => String(o?.value) === String(selectedBatch));
    if (found) {
      setSelectedBatchOption(found);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getBatchById(selectedBatch);
        const opt = toBatchSelectOption(resp?.data);
        if (!cancelled) setSelectedBatchOption(opt || { label: selectedBatch, value: selectedBatch });
      } catch (_) {
        if (!cancelled) setSelectedBatchOption({ label: selectedBatch, value: selectedBatch });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBatch, batchFilterOptions]);

  useEffect(() => {
    if (!selectedTrainer) {
      setSelectedTrainerOption(null);
      return;
    }
    const found = (trainerFilterOptions || []).find((o) => String(o?.value) === String(selectedTrainer));
    if (found) {
      setSelectedTrainerOption(found);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getTrainerByTrainerId(selectedTrainer);
        const opt = toTrainerSelectOption(resp?.data);
        if (!cancelled) setSelectedTrainerOption(opt || { label: selectedTrainer, value: selectedTrainer });
      } catch (_) {
        if (!cancelled) setSelectedTrainerOption({ label: selectedTrainer, value: selectedTrainer });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTrainer, trainerFilterOptions]);

  useEffect(() => {
    if (!selectedClassroom) {
      setSelectedClassroomOption(null);
      return;
    }
    const found = (classroomFilterOptions || []).find(
      (o) => String(o?.value) === String(selectedClassroom)
    );
    if (found) {
      setSelectedClassroomOption(found);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getClassroomById(selectedClassroom);
        const opt = toClassroomSelectOption(resp?.data);
        if (!cancelled) setSelectedClassroomOption(opt || { label: selectedClassroom, value: selectedClassroom });
      } catch (_) {
        if (!cancelled) setSelectedClassroomOption({ label: selectedClassroom, value: selectedClassroom });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedClassroom, classroomFilterOptions]);

  const fetchEventsForCurrentView = useCallback(
    async (range, overrideFilters = null) => {
      try {
        if (!range?.start || !range?.end) return;
        // Asking before the student's batches are known would ask for
        // everybody's. Held until they are; the effect below re-runs it.
        if (scopePending) return;

        const viewStart = moment(range.start).startOf('day').toISOString();
        const viewEnd = moment(range.end).endOf('day').toISOString();
        const effectiveTrainerId = overrideFilters
          ? overrideFilters.trainerId
          : isTrainerMode
            ? selectedTrainer || user?.id || undefined
            : selectedTrainer || undefined;
        /* A student's own batches narrow the query; the batch filter
           narrows it further when one is picked. The scope is not
           optional, so it wins: a filter cannot widen a calendar past
           what its reader is allowed to see. */
        const pickedBatchId = overrideFilters ? overrideFilters.batchId : selectedBatch || undefined;
        const scopeFilter = batchScope(pickedBatchId);

        // Everything the client needs to render -- trainer/batch/course/
        // classroom names included -- comes back resolved from the backend;
        // no separate lookups to run here.
        /* The grid shows whole weeks, so a month view also draws the last
           days of the month before and the first of the month after. The
           From/To filter defaults to the month itself, and the server
           clamps batch sessions to it -- so 1-4 Oct sat in the September
           grid as empty cells, while the day list (which asks for that
           one date) showed them full. When the filter is exactly the
           month, the grid asks for every date it draws. A range someone
           narrowed on purpose is left as they set it. */
        const pickedStart = overrideFilters ? overrideFilters.startDateFilter : (startDateFilter || undefined);
        const pickedEnd = overrideFilters ? overrideFilters.endDateFilter : (endDateFilter || undefined);
        const gridFirst = moment(range.start).format('YYYY-MM-DD');
        // FullCalendar's range end is exclusive.
        const gridLast = moment(range.end).subtract(1, 'day').format('YYYY-MM-DD');
        const isWholeMonth =
          Boolean(pickedStart && pickedEnd) &&
          moment(pickedStart).format('YYYY-MM-DD') ===
            moment(pickedStart).startOf('month').format('YYYY-MM-DD') &&
          moment(pickedEnd).format('YYYY-MM-DD') ===
            moment(pickedStart).endOf('month').format('YYYY-MM-DD');
        const gridStartDate = isWholeMonth && gridFirst < pickedStart ? gridFirst : pickedStart;
        const gridEndDate = isWholeMonth && gridLast > pickedEnd ? gridLast : pickedEnd;

        const res = await getCalendarEvents({
          viewStart,
          viewEnd,
          trainerId: effectiveTrainerId,
          ...scopeFilter,
          eventType: overrideFilters ? overrideFilters.eventType : eventTypeFilter,
          hidePastEvents: overrideFilters ? overrideFilters.hidePastEvents : hidePastEvents,
          startDateFilter: gridStartDate,
          endDateFilter: gridEndDate,
          classroom: overrideFilters ? overrideFilters.classroom : (selectedClassroom || undefined),
          includeFreeSlots: false,
        });

        const raw = Array.isArray(res?.data) ? res.data : [];
        const withoutFreeSlots = raw.filter((e) => e?.extendedProps?.type !== 'free_slot');
        setCalendarEvents(withoutFreeSlots);

        /* The number on a cell is how many of the things the reader is
           asking for land on that date -- every filter applied, the same
           ones this view was just fetched with. It comes from its own
           endpoint, GET /calendar/events/counts, which is authoritative
           because the server knows what the client does not: how a
           multi-day holiday spans dates, how a batch expands into daily
           sessions, which dates a holiday cancels. Only the counts cross
           the wire, not a second copy of the month.

           This is NOT the same number the day list reports. Opening a
           date shows every type on it, so a cell reading 2 can open onto
           5; the day list's header says so. See the note on
           dayListFilters below -- the two are different questions on
           purpose.

           If the counts call fails the badges fall back to the rows
           already in hand. That is the same arithmetic on a set the
           server already filtered, so it agrees with the endpoint on
           anything inside this view; what it cannot see is a multi-day
           holiday whose own start date sits outside the range, which is
           why it is the fallback and not the source. */
        const filtersForCounts = {
          viewStart,
          viewEnd,
          trainerId: effectiveTrainerId,
          ...scopeFilter,
          eventType: overrideFilters ? overrideFilters.eventType : eventTypeFilter,
          hidePastEvents: overrideFilters ? overrideFilters.hidePastEvents : hidePastEvents,
          startDateFilter: gridStartDate,
          endDateFilter: gridEndDate,
          classroom: overrideFilters ? overrideFilters.classroom : (selectedClassroom || undefined),
          includeFreeSlots: false,
        };

        try {
          const countRes = await getCalendarDayCounts(filtersForCounts);
          setApiDayCounts(countRes?.counts || {});
        } catch (err) {
          console.warn(
            '[calendar] per-day counts request failed; badges fall back to the fetched rows',
            err,
          );
          try {
            setApiDayCounts(
              Object.fromEntries(
                [...countByDay(withoutFreeSlots).entries()].map(([iso, v]) => [iso, v.n]),
              ),
            );
          } catch (inner) {
            /* A thrown date parse would otherwise take the whole month
               render down, and a calendar with no badges still shows the
               month. */
            console.warn(
              '[calendar] could not derive per-day counts from the fetched events either; day badges will be hidden',
              inner,
            );
            setApiDayCounts(null);
          }
        }
      } catch (_) {
        setCalendarEvents([]);
        setApiDayCounts(null);
      }
    },
    [
      endDateFilter,
      eventTypeFilter,
      hidePastEvents,
      isTrainerMode,
      selectedBatch,
      selectedClassroom,
      selectedTrainer,
      user?.id,
      startDateFilter,
      // The student's batch list arrives after the first render, so the
      // callback has to be rebuilt when it does -- otherwise the fetch
      // that runs on the next view change is still closed over the empty
      // scope this started with.
      batchScope,
      scopePending,
    ]
  );

  const fetchAllData = async () => {
    setLoading(false);
  };

  const handleDatesSet = (arg) => {
    const range = { start: arg?.start, end: arg?.end };
    setCurrentViewRange(range);

    const calendarApi = calendarRef.current?.getApi();
    const currentDate = calendarApi ? calendarApi.getDate() : new Date();
    const currentMonth = currentDate.getMonth();
    setSelectedMonthIndex(currentMonth);
    if (filtersApplied) fetchEventsForCurrentView(range);
  };

  // Check if any filter values are selected
  const hasFilterSelections = () => {
    return (
      selectedBatch ||
      selectedTrainer ||
      selectedClassroom ||
      startDateFilter ||
      endDateFilter ||
      eventTypeFilter !== 'all' ||
      availabilityFilter !== 'all'
    );
  };

  /* Escape shuts the sheet, and the page behind it stops scrolling --
     without the lock, dragging the sheet scrolls the calendar under it.
     Crossing back above 768px shuts it too: the panel is a block in the
     page at that width and a fixed sheet over it would be a duplicate. */
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) setFiltersOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFiltersOpen(false);
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  /* How many are set, for the badge on the phone's "Filters" button.
     Behind a sheet the panel is out of sight, so the button has to say
     whether anything is narrowing the calendar -- otherwise a student
     sees an empty month and has no way to tell that a filter, not the
     timetable, is why. `hidePastEvents` counts: it hides events too. */
  const activeFilterCount = [
    selectedBatch,
    selectedTrainer,
    selectedClassroom,
    startDateFilter,
    endDateFilter,
    eventTypeFilter !== 'all',
    availabilityFilter !== 'all',
    hidePastEvents,
  ].filter(Boolean).length;

  /* The student's batches resolve after the first fetch has already
     gone out, so the list in hand was scoped against an empty set --
     nothing but institution-wide entries. Refetch once, when the
     batches land, so the grid fills in rather than sitting empty until
     the reader changes month.

     Placed below fetchEventsForCurrentView and currentViewRange: both
     are consts this reads, and a hook above them would be reading a
     binding that does not exist yet. */
  // Keyed by who is being read, not a bare flag: staff swapping student
  // has to refetch, and a boolean would have said "already done".
  const pickedStudentKey = isStudentMode ? 'self' : String(pickedStudent?.email || pickedStudent?.id || '');
  const studentScopeFetched = useRef(null);
  useEffect(() => {
    if (!isStudentMode && !pickedStudent) return;
    if (!studentBatchesReady) return;
    if (studentScopeFetched.current === pickedStudentKey) return;
    if (!currentViewRange?.start || !currentViewRange?.end) return;
    studentScopeFetched.current = pickedStudentKey;
    fetchEventsForCurrentView(currentViewRange);
  }, [
    isStudentMode,
    pickedStudent,
    pickedStudentKey,
    studentBatchesReady,
    currentViewRange,
    fetchEventsForCurrentView,
  ]);

  // Button-based filter application
  const applyFilters = () => {
    setLoadingFilters(true);
    setTimeout(() => {
      setFiltersApplied(true); // Mark that filters have been applied
      if (currentViewRange?.start && currentViewRange?.end) {
        fetchEventsForCurrentView(currentViewRange);
      }
      setLoadingFilters(false);
    }, 500);
  };

  const clearFilters = () => {
    const calendarApi = calendarRef.current?.getApi();
    const currentDate = calendarApi ? calendarApi.getDate() : new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const start = moment().year(currentYear).month(currentMonth).startOf('month').format('YYYY-MM-DD');
    const end = moment().year(currentYear).month(currentMonth).endOf('month').format('YYYY-MM-DD');

    // Reset all filter states
    setSelectedBatch('');
    setSelectedBatchOption(null);
    setSelectedTrainer('');
    setSelectedTrainerOption(null);
    setSelectedClassroom('');
    setSelectedClassroomOption(null);
    setSelectedMonthIndex(currentMonth);
    setStartDateFilter(start);
    setEndDateFilter(end);
    setEventTypeFilter('all');
    setAvailabilityFilter('all');
    setHidePastEvents(true);
    setFiltersApplied(true);
    if (currentViewRange?.start && currentViewRange?.end) {
      fetchEventsForCurrentView(currentViewRange, {
        trainerId: '',
        batchId: '',
        eventType: 'all',
        hidePastEvents: true,
        startDateFilter: start,
        endDateFilter: end,
        classroom: '',
      });
    } else {
      setCalendarEvents([]); // Clear calendar events
    }
  };

  const [selectedMonthIndex, setSelectedMonthIndex] = useState(moment().month());

  const handleMonthChange = (event) => {
    const selectedMonth = Number.parseInt(event.target.value);
    setSelectedMonthIndex(selectedMonth);
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const currentYear = calendarApi.getDate().getFullYear();
      calendarApi.gotoDate(moment().year(currentYear).month(selectedMonth).date(1).toDate());
      setStartDateFilter(moment().year(currentYear).month(selectedMonth).startOf('month').format('YYYY-MM-DD'));
      setEndDateFilter(moment().year(currentYear).month(selectedMonth).endOf('month').format('YYYY-MM-DD'));
    } else {
      const currentYear = moment().year();
      setStartDateFilter(moment().year(currentYear).month(selectedMonth).startOf('month').format('YYYY-MM-DD'));
      setEndDateFilter(moment().year(currentYear).month(selectedMonth).endOf('month').format('YYYY-MM-DD'));
    }
  };

  /* The figure comes from the unfiltered count pass; the holiday flag
     still comes from the drawn events, which is the only place it is
     known. Taking the whole cell object from the count pass dropped the
     flag and with it the holiday styling. */
  const dayCounts = useMemo(() => {
    const local = countByDay(calendarEvents);

    /* The counts endpoint is handed the same batchId list as the events
       query -- it runs the same composition (calendar.service.js
       dayCounts -> events) -- so its figures and the rows the grid drew
       are answers to one question. This used to be counted locally for
       a scoped reader, because the browser was doing the narrowing and
       the server did not know about it. */
    if (!apiDayCounts) return local;

    const merged = new Map();
    for (const [iso, n] of Object.entries(apiDayCounts)) {
      merged.set(iso, { n, holiday: local.get(iso)?.holiday || false });
    }
    /* A day the count pass did not mention but the grid drew — keep it,
       so nothing silently loses its badge. */
    for (const [iso, cell] of local.entries()) {
      if (!merged.has(iso)) merged.set(iso, cell);
    }
    return merged;
  }, [apiDayCounts, calendarEvents]);

  /* What the day list re-queries with: the SCOPE filters only -- who and
     where. No eventType, so the request defaults to every type.

     The cell's number and this list answer two different questions on
     purpose, and it is worth being clear about which is which:

       the cell   how many of the things I am asking for land on this
                  date -- every filter the grid is under, counted
                  server-side in meta.countsByDay
       this list  what is on this date -- all of it, whatever type,
                  because once a day is picked the whole day is the
                  useful thing

     So a cell can read 2 and open onto 5. That is not drift; the modal's
     header says "5 entries - 2 match your filters" whenever the two
     differ and a filter is on. Scope still applies to both: "every type"
     is not "everybody's", so a calendar narrowed to one trainer opens
     onto that trainer's day.

     Adding eventType or category here would collapse the day list back
     onto the grid's narrowing and lose the whole-day view. */
  const dayListFilters = useMemo(
    () => ({
      trainerId: isTrainerMode
        ? selectedTrainer || user?.id || undefined
        : selectedTrainer || undefined,
      /* The same narrowing the grid is under, so the list a cell opens
         answers the question its number answered. */
      ...batchScope(selectedBatch || undefined),
      classroom: selectedClassroom || undefined,
      // Carried through so the day list hides past sessions exactly when
      // the counts do. Without it the cell and the list disagree on every
      // past date -- 0 on the cell, a full day of finished classes in the
      // list.
      hidePastEvents,
      // eventType is deliberately absent, so the request defaults to 'all'.
      // Opening a date answers "what is on this day", and that question does
      // not inherit the grid's narrowing: a cell showing 5 has to open onto
      // 5 rows, and the count the backend sends is taken across every type.
      // Leaving eventType in here is what made a cell read 5 and its list
      // show 2.
      //
      // The scope filters above stay. "Every type" is not "everybody's" --
      // a calendar narrowed to one trainer still opens onto that trainer's
      // day, and the counts are scoped the same way on the server.
      //
      // The date-range filters are gone for the same reason: the day list
      // pins itself to the clicked date, and a range that excluded it
      // would return nothing for a cell that shows a number.
    }),
    [
      hidePastEvents,
      isTrainerMode,
      selectedBatch,
      selectedClassroom,
      selectedTrainer,
      user?.id,
      batchScope,
    ],
  );

  // A neighbouring month's first day reads "Oct 1", so the cells after
  // 30 Sep say which month they belong to rather than looking like a
  // second run of small numbers.
  const renderDayCell = (arg) => {
    if (arg.view?.type === 'dayGridMonth' && arg.isOther && arg.date.getDate() === 1) {
      return moment(arg.date).format('MMM D');
    }
    return arg.dayNumberText;
  };

  const openDayList = (dateStr) => {
    Swal.close();
    setDayListDate(dateStr);
  };

  const handleDateClick = (dateClickInfo) => {
    const clickedDate = dateClickInfo?.date
      ? moment(dateClickInfo.date).format('YYYY-MM-DD')
      : String(dateClickInfo?.dateStr || '').split('T')[0];
    if (!clickedDate) return;
    // A day opens its list now, for everyone. Adding an event is a button
    // inside that list, so a reader who only wanted to look no longer lands
    // in a create form.
    openDayList(clickedDate);
  };

  const handleEventClick = async (eventClickInfo) => {
  const event = eventClickInfo.event;

  const calendarApi = calendarRef.current?.getApi();
  if (calendarApi) {
    calendarApi.unselect();
    document.body.click();
    setTimeout(() => {
      const popover = document.querySelector('.fc-more-popover');
      if (popover) {
        popover.style.display = 'none';
        popover.style.visibility = 'hidden';
      }
    }, 50);
  }

  // Opening one entry replaces another, rather than stacking on it. This
  // was Swal.close() when the dialog was a SweetAlert.
  setEventDetail(null);
  // And the day list, which is where most events are opened from. It was
  // left open: onAddEvent below closes it before opening the form, but
  // onOpenEvent did not, so opening an event from a date put a second
  // dialog over the first -- and then Edit closed the top one and left
  // the day list sitting under the form. Three dialogs live on this page
  // and none of them is meant to stack on another.
  setDayListDate(null);

  const eventType = event.extendedProps?.type;
  const isHoliday = event.extendedProps?.isHoliday || event.extendedProps?.category === 'Holiday';

  if (eventType === 'free_slot') {
    if (!canManageEvents) return;
    const date =
      event.start ? moment(event.start).format('YYYY-MM-DD') : String(event.startStr || '').split('T')[0];
    setSelectedDateForEvent(date || null);
    setSelectedEventId(null);
    setShowAddEventModal(true);
    return;
  }

  const eventData = event.extendedProps?.data;
  if (!eventType || !eventData) return;

  // A trainer may only edit the regular event they personally created;
  // admins can edit any of them. This mirrors the ownership check enforced
  // server-side in event.controller.js, so the button is purely UX -- the
  // backend is what actually keeps other trainers out.
  const currentUserId = String(user?.id || '');
  const eventCreatedBy = String(eventData?.createdBy || '');
  const isEventOwner = Boolean(eventCreatedBy) && eventCreatedBy === currentUserId;
  const canEditThisEvent =
    canManageEvents && eventType === 'event' && (isAdminMode || (isTrainerMode && isEventOwner));

  // Variables for data extraction
  let displayDate = 'NA';
  let displayDateShort = 'NA';
  let displayTime = 'NA';
  let endTime = '';
  let organizerInitials = 'NA';
  let trainerName = 'NA';
  let description = '';
  let agenda = '';
  let venue = '';
  let onlineLink = '';
  let batchInfo = '';
  let dayInfo = '';
  let courseLabel = '';
  let batchMeetingLink = '';
  let batchRecordedLectureLink = '';
  let batchStudyMaterialLink = '';
  let batchMeetingId = '';
  let batchPassword = '';

  /* ---------------- LOGIC FOR DIFFERENT EVENT TYPES ---------------- */

  if (eventType === 'batch_daily') {
    // Batch Daily Event Logic. calendar.service.js already resolves and
    // embeds the batch's classroom number, course name and trainer name --
    // no separate lookups needed here, just read what the backend sent.
    const batchRecord = event.extendedProps.batchData || eventData;
    const batchName = batchRecord?.name || event.extendedProps.batchName || 'NA';
    const dayNumber = event.extendedProps.dayNumber || '';
    const totalDays = event.extendedProps.totalDays || '';
    const batchTime = event.extendedProps.batchTime || 'NA';
    const classroom = event.extendedProps.venue || 'No location added';
    courseLabel = event.extendedProps.course?.name || 'No course assigned';
    const trainers = event.extendedProps.trainers || 'No trainer assigned';
    batchMeetingLink = normalizeCalendarUrl(batchRecord?.meetingLink);
    batchRecordedLectureLink = normalizeCalendarUrl(batchRecord?.recordedLectureLink);
    batchStudyMaterialLink = normalizeCalendarUrl(batchRecord?.studyMaterialLink);
    batchMeetingId = formatBatchMetaField(batchRecord?.meetingId);
    batchPassword = formatBatchMetaField(batchRecord?.password);

    // Date formatting
    if (event.start) {
      const dateObj = moment(event.start);
      displayDate = `${dateObj.format('dddd')}, ${dateObj.format('DD/MM/YYYY')}`;
      displayDateShort = `${dateObj.format('ddd DD/MM/YY')}`;
    }

    // Time extraction
    if (batchTime) {
      displayTime = batchTime;
    }

    // Day info
    if (dayNumber && totalDays) {
      dayInfo = `${dayNumber} of ${totalDays}`;
    }

    // Batch info. No description: the API's is "<mode> batch - Day N of
    // M", which only repeated the Mode and Day facts beside it.
    batchInfo = batchName;
    venue = classroom;
    trainerName =
      typeof trainers === 'object' ? trainers.name || trainers.id || 'No trainer assigned' : trainers;
  } else if (eventType === 'event') {
    // Regular Event or Holiday Logic
    const organizerName =
      eventData.organizerName || event.extendedProps?.organizerName || 'Organizer';

    // Get organizer initials
    organizerInitials = organizerName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);

    if (isHoliday) {
      // HOLIDAY EVENT LOGIC
      const startDateTime = moment(eventData.startDateTime);
      const endDateTime = moment(eventData.endDateTime);
      const isAllDay = event.extendedProps?.isAllDay;
      const holidayDescription = eventData.description || '';
      const affectedBatches = event.extendedProps?.batches?.join(', ') || 'All batches';

      // Date formatting for holidays
      if (isAllDay) {
        if (startDateTime.isSame(endDateTime, 'day')) {
          displayDate = `${startDateTime.format('DD/MM/YYYY')}`;
          displayDateShort = `${startDateTime.format('ddd DD/MM/YY')}`;
        } else {
          displayDate = `${startDateTime.format('DD/MM/YYYY')} to ${endDateTime.format('DD/MM/YYYY')}`;
          displayDateShort = `${startDateTime.format('ddd DD/MM/YY')} – ${endDateTime.format('ddd DD/MM/YY')}`;
        }
        displayTime = 'All Day';
      } else {
        // Time-specific holiday
        if (event.start) {
          const start = moment(event.start);
          const end = moment(event.end);
          displayDate = `${start.format('dddd')}, ${start.format('DD/MM/YYYY')}`;
          displayDateShort = `${start.format('ddd DD/MM/YY')}`;
          displayTime = start.format('hh:mm A');
          endTime = end.format('hh:mm A');
        }
      }

      description = holidayDescription;
      batchInfo = affectedBatches;

      // Trainer names are already resolved server-side.
      trainerName = event.extendedProps.trainerName || 'All trainers';
    } else {
      // REGULAR EVENT (non-holiday) LOGIC - FIXED TIME CONVERSION
      const eventDate = moment(eventData.date);

      // Parse time from event.time field (already in IST)
      const eventTimeStr = eventData.time; // e.g., "1970-01-01T14:30:00.000Z"

      // Extract hours and minutes
      const timeMatch = eventTimeStr.match(/T(\d{2}):(\d{2}):/);

      let hours = 10; // default 10 AM
      let minutes = 0;

      if (timeMatch) {
        hours = parseInt(timeMatch[1]);
        minutes = parseInt(timeMatch[2]);
      }

      // Combine date with time (already IST)
      const eventDateTime = eventDate.hour(hours).minute(minutes).second(0);

      const endDateTime = eventDateTime.clone().add(1, 'hour');

      // Format time in 12-hour format
      const format12Hour = (hour, minute) => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        const displayMinute = minute.toString().padStart(2, '0');
        return `${displayHour}:${displayMinute} ${period}`;
      };

      // Date and time formatting
      displayDate = `${eventDate.format('dddd')}, ${eventDate.format('DD/MM/YYYY')}`;
      displayDateShort = `${eventDate.format('ddd DD/MM/YY')}`;
      displayTime = format12Hour(hours, minutes);
      endTime = format12Hour(endDateTime.hour(), endDateTime.minute());

      // Additional fields. classroomNumber is the human-readable value
      // chosen at create time; trainerName/batchNames are resolved
      // server-side in calendar.service.js -- read them directly.
      venue = eventData.classroomNumber || 'No location added';
      description = eventData.description || '';
      agenda = eventData.agenda || '';
      onlineLink = eventData.onlineLink || '';
      trainerName = event.extendedProps.trainerName || 'NA';

      const relatedBatches = event.extendedProps.batchNames || [];
      if (relatedBatches.length > 0) {
        batchInfo = relatedBatches.join(', ');
      }
    }
  }

  /* ---------------- WHAT THE DIALOG READS ----------------
     A plain object, handed to <EventDetailModal>. It used to be 300
     lines of HTML string fed to Swal.fire and wired up afterwards by
     document.getElementById; the dialog is a component now, so all
     that is left here is deciding what to say. */

  const isRegularEvent = eventType === 'event' && !isHoliday;

  // Who a regular event is for (its batches) and who is running it (its
  // trainers) is organisational detail: useful to admins and trainers,
  // noise to a student, who only needs to know the event is on and how
  // to attend. Holidays and batch sessions are unaffected -- a student
  // does need to see which of their batches those apply to.
  const showAudienceSections = !isStudentMode;

  const when = [
    displayDateShort,
    displayTime && displayTime !== 'All Day'
      ? `${displayTime}${endTime ? ` – ${endTime}` : ''}`
      : displayTime,
  ]
    .filter((part) => part && part !== 'NA')
    .join(' · ');

  // Empty values are dropped rather than printed as "NA", so a sparse
  // entry reads short instead of listing what it does not have.
  const keep = (pairs) =>
    pairs.filter(([, value]) => value && value !== 'NA' && value !== '');

  /* Several trainers on one entry read as a run-on line in the facts and a
     second list in the body. With more than one, they move to their own
     tab in the popup; one trainer stays a fact. A batch session has one
     trainer by construction. */
  const trainerList =
    eventType === 'batch_daily' ? [] : event.extendedProps?.trainerNames || [];
  const manyTrainers = trainerList.length > 1;

  /* The head's line: what places the entry. The title already names the
     batch, so a "Batch" fact beside it only said it twice. */
  const ident = keep(
    eventType === 'batch_daily'
      ? [
          ['Batch code', event.extendedProps.batchCode],
          ['Course', courseLabel],
          ['When', when || displayDate],
          ['Day', dayInfo],
        ]
      : [
          ['When', when || displayDate],
          ['Venue', isHoliday ? '' : venue],
        ],
  );

  const facts = keep(
    eventType === 'batch_daily'
      ? [
          ['Trainer', trainerName],
          ['Venue', venue],
          ['Mode', event.extendedProps.mode],
        ]
      : isHoliday
        ? [
            ['Affected batches', summarizeNameList(batchInfo)],
            // More than one trainer gets its own tab (see `trainers` below)
            // rather than a comma-joined line here.
            ['Trainers', manyTrainers ? '' : trainerName],
          ]
        : [['Trainer', manyTrainers ? '' : trainerName]],
  );

  const audience =
    isRegularEvent && showAudienceSections
      ? [
          { label: 'Batches', names: event.extendedProps.batchNames || [] },
          { label: 'Trainers', names: manyTrainers ? [] : trainerList },
        ].filter((group) => group.names.length > 0)
      : [];

  // A batch session's trainer is already the Trainer fact; the card under
  // it repeated the name with "is conducting this session". Only an
  // event's organiser, who carries an email and a phone, gets one.
  const organizer = isRegularEvent
    ? {
        initials: organizerInitials,
        name: eventData.organizerName || 'Organizer',
        line: 'invited you',
        email: !isStudentMode ? eventData.organizerEmail || '' : '',
        phone: !isStudentMode ? eventData.organizerPhone || '' : '',
      }
    : null;

  const prose = keep([
    ['Description', description],
    ['Agenda', agenda],
    ['Highlights', eventData.highlights],
  ]);

  const links = [
    ['Meeting link', batchMeetingLink],
    ['Recorded lecture', batchRecordedLectureLink],
    ['Study material', batchStudyMaterialLink],
  ]
    .filter(([, url]) => Boolean(url))
    .map(([label, url]) => ({ label, url }));

  const credentials = keep([
    ['Meeting ID', batchMeetingId],
    ['Password', batchPassword],
  ]);

  setEventDetail({
    kind: eventType === 'batch_daily' ? 'batch' : isHoliday ? 'holiday' : 'event',
    title:
      eventType === 'batch_daily'
        ? batchInfo || 'Daily batch session'
        : eventData.name || event.title || 'Event',
    ident,
    facts,
    trainers: manyTrainers ? trainerList : [],
    audience,
    organizer,
    prose,
    links,
    credentials,
    actions: {
      onlineLink: eventType !== 'batch_daily' ? onlineLink : '',
      canViewAttendance: eventType === 'batch_daily',
      canEditBatch: eventType === 'batch_daily' && canManageEvents,
      canEditEvent: eventType === 'event' && canEditThisEvent,
      batchId: String(event.extendedProps?.batchData?.id || eventData?.id || ''),
      eventId: String(eventData?.id || event.id || ''),
    },
  });
};

  const handleModalClose = () => {
    setShowAddEventModal(false);
    setSelectedDateForEvent(null);
    setSelectedEventId(null);
  };

  const handleEventSaved = () => {
    // Saved: the dialog shows so in place and closes itself after a moment.
    // The record changed, so the view popup still open behind this form
    // would be showing the old one. It goes; the form stays.
    setEventDetail(null);
    // Refresh the calendar data only if filters are applied
    if (filtersApplied) {
      applyFilters();
    }
  };

  // The event form's save is confirmed in its own dialog, not a toast.
  const { done: eventFormDone } = usePopupDone(Boolean(canManageEvents && showAddEventModal));

  /* One dropdown, rendered into the gate before a student is named and
     into the strip above the calendar afterwards. */
  const studentPicker = (
    <AsyncSelectDropdown
      options={studentOptions}
      value={
        pickedStudent
          ? [{ value: pickedStudent.email || pickedStudent.id, label: pickedStudent.name }]
          : []
      }
      onChange={handleStudentPicked}
      placeholder="Search by name, email or phone..."
      isMulti={false}
      isSearchable
      /* No searchInControl: /student-profile and /student-id-card draw
         this same "pick a student" dropdown with the search in the MENU,
         which is the component's default. Turning it on here put the
         search box in the control itself and made the same question look
         like a different widget on the two pages that ask it. */
      isLoading={studentOptionsLoading}
      onMenuOpen={() => {
        if (studentOpenedRef.current) return;
        studentOpenedRef.current = true;
        loadStudentOptions(1, '');
      }}
      onInputChange={(value) => {
        studentOpenedRef.current = true;
        setStudentSearch(value);
        setStudentHasMore(true);
        loadStudentOptions(1, value);
      }}
      onMenuScrollToBottom={() => {
        if (!studentHasMore || studentFetchRef.current) return;
        loadStudentOptions(studentPage + 1, studentSearch, true);
      }}
      onRefresh={() => {
        studentOpenedRef.current = true;
        setStudentSearch('');
        setStudentHasMore(true);
        setStudentOptions([]);
        loadStudentOptions(1, '');
      }}
      loadingMessage="Loading students..."
      noOptionsMessage="No students found"
      noSearchResultsMessage="No students matched your search"
    />
  );

  /* ─── Staff on the student calendar, with nobody named ───
     The gate comes BEFORE the calendar, not beside it. A calendar that
     loads first and narrows second has already drawn the wrong answer,
     and on this page that answer is every batch in the institution --
     which is exactly what a reader opening "the student calendar"
     should never see.

     Same block, class for class, as /student-id-card and
     /student-profile: sp-gate from StudentProfile.css rather than a
     copy, so the three cannot drift apart. */
  if (needsStudentPick && !pickedStudent) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <div className="sp-gate">
            <span className="sp-gate-mark">
              <CalendarDays size={22} aria-hidden="true" />
            </span>
            <h1 className="sp-gate-title">Pick a student</h1>
            <p className="sp-gate-text">
              Choose a student to see their calendar — their batches&apos; classes,
              the events addressed to them, and the holidays that affect them.
            </p>
            <div className="sp-gate-field">{studentPicker}</div>
            <span className="sp-gate-tip">
              <Search size={14} aria-hidden="true" />
              Search by name or email.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <div>
          <div  />
          <p>Loading Calendar…</p>
        </div>
        </div>
    );
  }

  return (
    <div className="pg cal-page">

      <header className="pg-head">
        <div className="pg-head-main">
          <h4 className="pg-title">
            {/* Whose calendar this is, said in the title. Staff reading a
                student's calendar were shown "Calendar" -- the same word
                the whole-institution view uses -- with nothing on the
                page to say the rows belonged to one student. */}
            {needsStudentPick
              ? `${pickedStudent?.name || 'Student'} · calendar`
              : isAdminMode
                ? 'Calendar'
                : isTrainerMode
                  ? 'My schedule'
                  : 'Class calendar'}
          </h4>
          {needsStudentPick && (
            <p className="pg-sub">
              Their batches&apos; classes, the events addressed to them, and the
              holidays that affect them.
            </p>
          )}
        </div>
        <div className="pg-head-actions">
          {canExport && (
            <button
              type="button"
              className="pg-btn"
              onClick={exportToExcel}
              disabled={!filtersApplied || calendarEvents.length === 0}
            >
              Export
            </button>
          )}
          {canManageEvents && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => {
                setSelectedDateForEvent(null);
                setSelectedEventId(null);
                setShowAddEventModal(true);
              }}
            >
              Add event
            </button>
          )}
        </div>
      </header>

      {/* Who is being read, and the control that swaps them -- so a
          second student is one choice away rather than a page back.
          The same strip the student dashboard's staff view carries. */}
      {needsStudentPick && (
        <section className="pg-filters cal-student-strip" aria-label="Viewing student">
          <div className="pg-field pg-field--grow">
            <label className="pg-label" htmlFor="cal-student-pick">
              Viewing student
            </label>
            <div id="cal-student-pick">{studentPicker}</div>
          </div>
          <button
            type="button"
            className="pg-btn"
            onClick={() => {
              setPickedStudent(null);
              setCalendarEvents([]);
              setApiDayCounts(null);
            }}
          >
            Clear
          </button>
        </section>
      )}

      {/* Everything that narrows the calendar, in one panel.

          On a phone that panel is a bottom sheet: the trigger, the scrim
          and the sheet's own header below are display:none above 768px,
          where .cal-filter-body is just a block in the page and this
          reads exactly as it always did. */}
      <section className="pg-filters cal-filters" aria-label="Filter the calendar">
        <button
          type="button"
          className="cal-filter-open"
          onClick={() => setFiltersOpen(true)}
          aria-expanded={filtersOpen}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          Add filters
          {activeFilterCount > 0 && (
            <span className="cal-filter-count">{activeFilterCount}</span>
          )}
        </button>

        {filtersOpen && (
          <div
            className="cal-filter-scrim"
            role="button"
            tabIndex={0}
            aria-label="Close filters"
            onClick={() => setFiltersOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setFiltersOpen(false);
            }}
          />
        )}

        <div className={`cal-filter-body${filtersOpen ? ' is-open' : ''}`}>
          <header className="cal-filter-sheet-head">
            <span className="cal-filter-grip" aria-hidden="true" />
            <h5 className="cal-filter-sheet-title">Filters</h5>
            <button
              type="button"
              className="cal-filter-sheet-close"
              onClick={() => setFiltersOpen(false)}
              aria-label="Close filters"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="18" height="18" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </header>

        <div className="cal-filter-grid">
                  {/* Month Selector */}
                  <div className="cal-filter-field">
                    <label htmlFor="month-selector" >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      Jump to Month
                    </label>
                    <select id="month-selector"  value={selectedMonthIndex} onChange={handleMonthChange}>
                      {moment.months().map((month, index) => (
                        <option key={index} value={index}>{month}</option>
                      ))}
                    </select>
                  </div>

                  {/* Hide Past Events Toggle */}
                  <div className="cal-filter-field">
                    <label>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      Past Events
                    </label>
                    <label className="cal-switch-label">
                      <input
                        type="checkbox"
                        className="cal-switch-input"
                        id="hidePastEvents"
                        checked={hidePastEvents}
                        onChange={(e) => setHidePastEvents(e.target.checked)}
                      />
                      {/* The knob used to be an unclassed empty <span> beside a
                          display:none checkbox, so nothing was drawn and the row read
                          identically whether the filter was on or off. */}
                      <span className="cal-switch" aria-hidden="true">
                        <span className="cal-switch-dot" />
                      </span>
                      <span>Hide past events</span>
                      <span className="cal-switch-state">{hidePastEvents ? 'On' : 'Off'}</span>
                    </label>
                  </div>

                  {/* Event Type Filter */}
                  <div className="cal-filter-field">
                    <label htmlFor="event-type-filter" >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                      Event Type
                    </label>
                    <select id="event-type-filter"  value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}>
                      <option value="all">All Events</option>
                      <option value="batch">Batch</option>
                      <option value="event">Regular</option>
                    </select>
                  </div>

                  {/* Batch Filter */}
                  <div className="cal-filter-field">
                    <label htmlFor="batch-filter" >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                      Filter by Batch
                    </label>
                    <AsyncSelectDropdown
                      options={[{ label: 'All Batches', value: '' }, ...batchFilterOptions]}
                      defaultOptions={[{ label: 'All Batches', value: '' }, ...batchFilterOptions]}
                      loadOptions={loadBatchFilterOptions}
                      onMenuOpen={handleBatchFilterMenuOpen}
                      onMenuScrollToBottom={handleBatchFilterMenuScrollToBottom}
                      isLoading={batchFilterLoading || batchFilterFetchingMore}
                      value={
                        selectedBatch
                          ? [selectedBatchOption || { label: selectedBatch, value: selectedBatch }]
                          : []
                      }
                      onChange={(opts) =>
                        setSelectedBatch(
                          Array.isArray(opts) && opts.length
                            ? opts[opts.length - 1].value || ''
                            : '',
                        )
                      }
                      placeholder="All batches"
                      isMulti={false}
                      searchInControl={true}
                    />
                  </div>

                  {/* Trainer Filter. Not drawn for a trainer: the API locks a
                      trainer's calendar to their own record whatever is
                      picked here, so offering "All Trainers" and every
                      other name was a filter that silently did nothing. */}
                  {!isTrainerMode && (
                  <div className="cal-filter-field">
                    <label htmlFor="trainer-filter" >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      Filter by Trainer
                    </label>
                    <AsyncSelectDropdown
                      options={trainerPickerOptions}
                      defaultOptions={trainerPickerOptions}
                      loadOptions={studentScopedView ? undefined : loadTrainerFilterOptions}
                      onMenuOpen={studentScopedView ? undefined : handleTrainerFilterMenuOpen}
                      onMenuScrollToBottom={
                        studentScopedView ? undefined : handleTrainerFilterMenuScrollToBottom
                      }
                      isLoading={
                        studentScopedView
                          ? !studentBatchesReady
                          : trainerFilterLoading || trainerFilterFetchingMore
                      }
                      value={
                        studentScopedView
                          ? selectedTrainer
                            ? studentScopedTrainers.filter(
                                (o) => String(o.value) === String(selectedTrainer),
                              )
                            : []
                          : isAdminMode || isTrainerMode
                            ? selectedTrainer
                              ? [selectedTrainerOption || { label: selectedTrainer, value: selectedTrainer }]
                              : []
                            : [{ label: user?.name || 'Current User', value: '' }]
                      }
                      onChange={(opts) =>
                        setSelectedTrainer(
                          Array.isArray(opts) && opts.length
                            ? opts[opts.length - 1].value || ''
                            : '',
                        )
                      }
                      placeholder={
                        studentScopedView && studentBatchesReady && !studentScopedTrainers.length
                          ? 'No trainer on this student\u2019s batches'
                          : 'All trainers'
                      }
                      isMulti={false}
                      isDisabled={
                        studentScopedView
                          ? !studentBatchesReady || !studentScopedTrainers.length
                          : !isAdminMode && !isTrainerMode
                      }
                      searchInControl={true}
                    />
                  </div>
                  )}

                  {/* Classroom Filter */}
                  <div className="cal-filter-field">
                    <label htmlFor="classroom-filter" >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      Filter by Classroom
                    </label>
                    <AsyncSelectDropdown
                      options={[{ label: 'All Classrooms', value: '' }, ...classroomFilterOptions]}
                      defaultOptions={[{ label: 'All Classrooms', value: '' }, ...classroomFilterOptions]}
                      loadOptions={loadClassroomFilterOptions}
                      onMenuOpen={handleClassroomFilterMenuOpen}
                      onMenuScrollToBottom={handleClassroomFilterMenuScrollToBottom}
                      isLoading={classroomFilterLoading || classroomFilterFetchingMore}
                      value={
                        selectedClassroom
                          ? [selectedClassroomOption || { label: selectedClassroom, value: selectedClassroom }]
                          : []
                      }
                      onChange={(opts) =>
                        setSelectedClassroom(
                          Array.isArray(opts) && opts.length
                            ? opts[opts.length - 1].value || ''
                            : '',
                        )
                      }
                      placeholder="All classrooms"
                      isMulti={false}
                      searchInControl={true}
                    />
                  </div>

                  {/* Date Filters */}
                  <div className="cal-filter-field cal-filter-field--wide">
                    <label htmlFor="start-date-filter" >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      From Date
                    </label>
                    <input type="date" id="start-date-filter"  value={startDateFilter} onChange={(e) => setStartDateFilter(e.target.value)} onClick={(e) => { try { e.target.showPicker(); } catch (err) {} }} />
                  </div>

                  <div className="cal-filter-field cal-filter-field--wide">
                    <label htmlFor="end-date-filter" >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      To Date
                    </label>
                    <input type="date" id="end-date-filter"  value={endDateFilter} onChange={(e) => setEndDateFilter(e.target.value)} onClick={(e) => { try { e.target.showPicker(); } catch (err) {} }} />
                  </div>

        </div>

        <div className="pg-filters-actions cal-filter-actions">
          <button
            type="button"
            className="pg-btn pg-btn--main"
            onClick={() => {
              applyFilters();
              setFiltersOpen(false);
            }}
            disabled={loadingFilters || !hasFilterSelections()}
          >
            {loadingFilters ? (
              <>
                <span className="pg-spinner att-spinner" aria-hidden="true" />
                Applying...
              </>
            ) : (
              'Apply filters'
            )}
          </button>
          <button
            type="button"
            className="pg-btn"
            onClick={clearFilters}
            disabled={loadingFilters || !hasFilterSelections()}
          >
            Clear
          </button>
        </div>
        </div>

        {/* What is currently narrowing the calendar, as chips. Kept on
            the page rather than inside the sheet: once the sheet is shut
            they are the only thing saying why the month looks the way it
            does. */}
        {hasFilterSelections() && (
          <div className="cal-active">
            <span className="cal-active-label">Filtering by</span>
            {eventTypeFilter !== 'all' && (
              <span className="pg-pill">Type: {eventTypeFilter}</span>
            )}
            {selectedBatch && (
              <span className="pg-pill">
                Batch: {selectedBatchOption?.label || selectedBatch}
              </span>
            )}
            {selectedTrainer && (
              <span className="pg-pill">
                Trainer: {selectedTrainerOption?.label || selectedTrainer}
              </span>
            )}
            {selectedClassroom && (
              <span className="pg-pill">
                Classroom: {selectedClassroomOption?.label || selectedClassroom}
              </span>
            )}
            {startDateFilter && (
              <span className="pg-pill">From {startDateFilter}</span>
            )}
            {endDateFilter && <span className="pg-pill">To {endDateFilter}</span>}
            {availabilityFilter !== 'all' && (
              <span className="pg-pill">
                {availabilityFilter === 'available' ? 'Available' : 'Unavailable'}
              </span>
            )}
            {hidePastEvents && <span className="pg-pill">Hiding past</span>}
          </div>
        )}
      </section>

        {/* Calendar or Filter Message */}
        {!filtersApplied ? (
          <div className="pg-state">
            <p className="pg-state-title">Nothing loaded yet</p>
            <p className="pg-state-text">
              Choose what you want to see above, then apply the filters.
            </p>
          </div>
        ) : (
          <>
            {/* What the colours mean, and the hours the grid covers. */}
            <div className="cal-legend">
              {[
                { dot: COLORS.BATCH, label: 'Batches' },
                { dot: COLORS.EVENT, label: 'Events' },
                { dot: COLORS.HOLIDAY, label: 'Holidays' },
                { dot: COLORS.MOCK_INTERVIEW, label: 'Mock interviews' },
              ].map(({ dot, label }) => (
                <span className="cal-leg" key={label}>
                  <span className="cal-leg-dot" style={{ background: dot }} />
                  {label}
                </span>
              ))}
              <span className="cal-leg-hours">
                {INSTITUTION_START}:00 – {INSTITUTION_END}:00
              </span>
            </div>

            {/* Calendar. "+N more" opens the day list, the same dialog a
                click on the cell opens, rather than FullCalendar's own
                popover. FullCalendar cannot be told that: a moreLinkClick
                that returns nothing still opens its popover, and a view
                name zooms the grid. So the click is caught on the way
                down, before the link's own handler sees it. */}
            <div
              className="cal-card"
              onClickCapture={(e) => {
                const link = e.target.closest?.('.fc-more-link');
                if (!link) return;
                const date = link.closest('[data-date]')?.getAttribute('data-date');
                if (!date) return;
                e.preventDefault();
                e.stopPropagation();
                openDayList(date);
              }}
            >
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                editable={canManageEvents}
                selectable={canManageEvents}
                events={calendarEvents}
                datesSet={handleDatesSet}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventDidMount={paintEventCotton}
                eventContent={renderEventChip}
                dayCellContent={renderDayCell}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay',
                }}
                height="auto"
                contentHeight="auto"
                eventDisplay="block"
                /* The month grid is chips again: the first three entries
                   of a day, each with its time and title, and "+N more"
                   under them for the rest. It was a single count per
                   cell, which said how much was on and never what. */
                dayMaxEvents={3}
                moreLinkClick="popover"
                slotMinTime="00:00:00"
                slotMaxTime="24:00:00"
                slotDuration="01:00:00"
                slotLabelInterval="01:00:00"
                // allDaySlot={false}
                nowIndicator={true}
                scrollTime="08:00:00"
                eventOverlap={false}
                slotEventOverlap={false}
                allDaySlot={true} // Ensure all-day slot is shown
                allDayText="All Day"
                eventMaxStack={100}
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: 'short',
                }}
                eventConstraint={{
                  start: '00:00',
                  end: '24:00',
                }}
                // timeZone="UTC"
              />
            </div>
          </>
        )}

        {/* The day list: everything on one date, in rows that open. */}
        {dayListDate && (
          <DayEventsModal
            date={dayListDate}
            filters={dayListFilters}
            /* The cell's own number: how many of this date's entries match
               the filters the grid is under. The modal lists every type,
               so when these differ it has to account for the difference
               rather than silently show more than the cell promised. */
            total={dayCounts.get(dayListDate)?.n || 0}
            filtersActive={activeFilterCount > 0}
            canAdd={canManageEvents}
            onAddEvent={(d) => {
              setDayListDate(null);
              setSelectedDateForEvent(d);
              setSelectedEventId(null);
              setShowAddEventModal(true);
            }}
            onOpenEvent={(ev) => handleEventClick({ event: ev })}
            onClose={() => setDayListDate(null)}
          />
        )}

        {/* One entry, opened from a chip. Its own portal, like the day
            list above it -- three dialogs live on this page and none of
            them is ever deliberately stacked on another. */}
        <EventDetailModal
          detail={eventDetail}
          onClose={() => setEventDetail(null)}
          onViewAttendance={() => {
            setEventDetail(null);
            navigate('/list-attendance');
          }}
          onEditBatch={() => {
            const batchId = eventDetail?.actions?.batchId;
            setEventDetail(null);
            if (batchId) navigate(`/add-batch/${batchId}`);
          }}
          onEditEvent={() => {
            const eventId = eventDetail?.actions?.eventId;
            if (!eventId) return;
            setSelectedDateForEvent(null);
            setSelectedEventId(eventId);
            setShowAddEventModal(true);
          }}
        />

        {/* Add Event Modal — rendered via portal so it always overlays the full viewport */}
        {canManageEvents && showAddEventModal && createPortal(
          <div
            className="pg-modal pg-modal--over"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cal-event-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) handleModalClose();
            }}
          >
            <div className="pg-modal-panel pg-modal-panel--xl pg-modal-panel--form">
              <header className="pg-modal-head">
                <div className="pg-modal-heading">
                  <span className="pg-modal-eyebrow">Calendar</span>
                  <h3 className="pg-modal-title" id="cal-event-title">
                    {selectedEventId ? 'Edit event' : 'Add event'}
                  </h3>
                </div>
                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={handleModalClose}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                {eventFormDone ? (
                  <PopupDone message={eventFormDone} onClose={handleModalClose} />
                ) : (
                  <AddEventModal
                    selectedDate={selectedDateForEvent}
                    selectedEventId={selectedEventId}
                    userRole="admin"
                    onEventSaved={handleEventSaved}
                    onCancel={handleModalClose}
                  />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AdminCalendar;
